/**
 * The Farkle state machine. One turn is: throw, set aside, decide.
 *
 * Three things run this exact file — the solo game in your browser, the online
 * room on the droplet, and the bot simulator — so it holds no timers, no
 * sockets, and no `Date.now()`. Every deadline in this game (the beat you get to
 * look at a FARKLE stamp, the pause before a bot moves) belongs to whoever is
 * driving the engine, because only they know whether a human is watching.
 *
 * State is plain JSON throughout: no Sets, no Maps, no class instances. That is
 * not an accident of style — it is what lets the server snapshot a room mid-game
 * to disk on SIGTERM and hand every player their dice back after a deploy.
 */

import { DICE_COUNT, isFarkle, isLegalKeep, scoreKeep } from './scoring.js';

export class RuleError extends Error {
  constructor(reason: string) {
    super(reason);
    this.name = 'RuleError';
  }
}

export type Phase =
  /** The dice are in the cup. The only move is to throw them. */
  | 'idle'
  /** Dice on the felt, at least one of them scoring. Set some aside. */
  | 'pick'
  /** Nothing in that throw scored. The turn is over and the points are gone. */
  | 'farkle'
  | 'over';

export type DieState =
  /** Thrown, still in play, not chosen. */
  | 'live'
  /** Chosen from the current throw, not yet committed. */
  | 'held'
  /** Set aside on an earlier throw this turn. Locked. */
  | 'set';

export interface DieSlot {
  face: number;
  state: DieState;
}

export interface Player {
  id: string;
  name: string;
  isBot: boolean;
  score: number;
  /** Cleared the opening threshold at least once. */
  onBoard: boolean;
  /** Every turn's banked total, farkles included as 0. Drives the sparkline. */
  turns: number[];
}

export interface SetupPlayer {
  id: string;
  name: string;
  isBot: boolean;
}

export interface Rules {
  /** Points that end the game. */
  target: number;
  /** Points needed in ONE turn before your score counts at all. 0 disables it. */
  threshold: number;
  /** Once someone hits the target, everyone else gets one more turn. */
  finalRound: boolean;
  /**
   * House rule: if the player before you banked with dice still in the cup, you
   * may pick those dice up instead of throwing six, and start your turn on a copy
   * of their score. They keep theirs. Farkle on that throw and you get nothing.
   */
  piggyback: boolean;
}

export const DEFAULT_RULES: Rules = {
  target: 10_000,
  threshold: 500,
  finalRound: true,
  piggyback: true,
};

/**
 * What the player before you left on the felt.
 *
 * Present only between a bank and the next player's first throw, and only if
 * they stopped with dice still in the cup. `kept` is what is showing on the
 * table, which is worth drawing: taking someone's 1,100 feels different when you
 * can see the three dice it came off.
 */
export interface Carry {
  /** The faces they set aside, still on the felt. */
  kept: number[];
  /** Dice left in the cup — always 1..5, since clearing all six leaves nothing. */
  dice: number;
  /** A COPY of what they banked. Taking it does not take it away from them. */
  points: number;
  fromId: string;
  fromName: string;
}

export type LogKind = 'roll' | 'keep' | 'hot' | 'bank' | 'farkle' | 'win' | 'carry';

export interface LogEntry {
  kind: LogKind;
  playerId: string;
  /** Already-composed, because the server and three clients would compose it
   *  four subtly different ways otherwise. */
  text: string;
  points?: number;
}

const LOG_MAX = 40;

export interface GameState {
  players: Player[];
  /** Index into `players`. Turn order is fixed for the game. */
  turn: number;
  round: number;
  phase: Phase;
  dice: DieSlot[];
  /** Banked-in-principle: scored from dice already set aside this turn. */
  turnPoints: number;
  /** How many times the player has cleared all six dice this turn. */
  hotStreak: number;
  rules: Rules;
  /**
   * The previous player's leftovers, if the house plays the piggyback rule and
   * they stopped short. Null the instant this turn's first throw happens, taken
   * or not — the offer is only good while the dice are still lying there.
   */
  carry: Carry | null;
  /** Whoever reached the target and started the last lap. */
  closerId: string | null;
  winnerIds: string[];
  log: LogEntry[];
  /**
   * Increments on every throw. The client keys its tumble animation on this
   * rather than on the faces — three 4s twice in a row is a real thing that
   * happens, and dice that do not move because the numbers matched look broken.
   */
  throwId: number;
  /** PRNG state. Stripped from every view; see `viewFor`. */
  rng: number;
}

/** What a client is allowed to see. Which, in Farkle, is everything but the seed. */
export type View = Omit<GameState, 'rng'>;

// ── randomness ──────────────────────────────────────────────────────────────

/**
 * mulberry32. Small, fast, and — the point — its whole state is one integer, so
 * a game in progress round-trips through `JSON.stringify` without losing its
 * place in the sequence.
 *
 * The seed never leaves the server. It is not a secret worth much (knowing it
 * predicts the next throw, which in a press-your-luck game is the whole thing),
 * so `viewFor` drops it rather than trusting every call site to remember.
 */
function random(g: GameState): number {
  g.rng = (g.rng + 0x6d2b79f5) | 0;
  let t = Math.imul(g.rng ^ (g.rng >>> 15), 1 | g.rng);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
}

function d6(g: GameState): number {
  return 1 + Math.floor(random(g) * 6);
}

/** A stable 32-bit seed from a string, for reproducible test games. */
export function seedFrom(text: string): number {
  let h = 2166136261;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

// ── construction ────────────────────────────────────────────────────────────

export function newGame(setup: SetupPlayer[], seed: number, rules: Rules = DEFAULT_RULES): GameState {
  if (setup.length < 1) throw new RuleError('a game needs a player');
  const clean: Rules = {
    target: clampInt(rules.target, 1000, 100_000, DEFAULT_RULES.target),
    threshold: clampInt(rules.threshold, 0, 5000, DEFAULT_RULES.threshold),
    finalRound: !!rules.finalRound,
    piggyback: !!rules.piggyback,
  };
  return {
    players: setup.map((p) => ({
      id: p.id,
      name: p.name,
      isBot: p.isBot,
      score: 0,
      // With no threshold everyone starts on the board, which is what makes the
      // rest of the code able to ignore the setting entirely.
      onBoard: clean.threshold === 0,
      turns: [],
    })),
    turn: 0,
    round: 1,
    phase: 'idle',
    dice: freshDice(),
    turnPoints: 0,
    hotStreak: 0,
    rules: clean,
    carry: null,
    closerId: null,
    winnerIds: [],
    log: [],
    throwId: 0,
    rng: seed >>> 0,
  };
}

function clampInt(value: number, lo: number, hi: number, fallback: number): number {
  if (!Number.isFinite(value)) return fallback;
  const n = Math.round(value);
  return n < lo || n > hi ? fallback : n;
}

function freshDice(): DieSlot[] {
  return Array.from({ length: DICE_COUNT }, () => ({ face: 1, state: 'live' as DieState }));
}

// ── reading the state ───────────────────────────────────────────────────────

export function current(g: GameState): Player {
  const p = g.players[g.turn];
  if (!p) throw new RuleError('no player is on turn');
  return p;
}

/** Faces of the dice thrown in the current throw and not yet chosen. */
export function liveFaces(g: GameState): number[] {
  return g.dice.filter((d) => d.state === 'live').map((d) => d.face);
}

/** Faces the player has selected from the current throw. */
export function heldFaces(g: GameState): number[] {
  return g.dice.filter((d) => d.state === 'held').map((d) => d.face);
}

/**
 * What the current selection pays, or null if it is not a legal thing to set
 * aside. Null is what greys out both buttons.
 */
export function heldScore(g: GameState): number | null {
  const held = heldFaces(g);
  if (held.length === 0) return 0;
  return scoreKeep(held);
}

/** Everything the turn is worth right now: committed, plus the live selection. */
export function pendingTotal(g: GameState): number {
  return g.turnPoints + (heldScore(g) ?? 0);
}

/** Would banking right now actually put points on the board? */
export function canBank(g: GameState): boolean {
  if (g.phase !== 'pick') return false;
  const held = heldFaces(g);
  if (held.length === 0 || !isLegalKeep(held)) return false;
  const player = current(g);
  const total = pendingTotal(g);
  if (total <= 0) return false;
  return player.onBoard || total >= g.rules.threshold;
}

/** Is throwing again a legal move from here? */
export function canRollAgain(g: GameState): boolean {
  if (g.phase === 'idle') return true;
  if (g.phase !== 'pick') return false;
  const held = heldFaces(g);
  return held.length > 0 && isLegalKeep(held);
}

/** Dice that would go back in the cup if they threw again. Six means hot dice. */
export function diceAfterKeep(g: GameState): number {
  const left = g.dice.filter((d) => d.state === 'live').length;
  return left === 0 ? DICE_COUNT : left;
}

/**
 * May the player on turn pick up what the last one left?
 *
 * The on-board requirement is the whole reason this rule is not simply free
 * points. Inheriting a total you could not have banked anyway would let someone
 * open on somebody else's turn, and the opener is the one thing in Farkle you are
 * supposed to earn yourself.
 */
export function canPiggyback(g: GameState): boolean {
  if (g.phase !== 'idle' || !g.carry) return false;
  return current(g).onBoard;
}

// ── moves ───────────────────────────────────────────────────────────────────

function requireTurn(g: GameState, playerId: string): Player {
  if (g.phase === 'over') throw new RuleError('the game is over');
  const player = current(g);
  if (player.id !== playerId) throw new RuleError('not your turn');
  return player;
}

function log(g: GameState, entry: LogEntry) {
  g.log.push(entry);
  if (g.log.length > LOG_MAX) g.log.splice(0, g.log.length - LOG_MAX);
}

/**
 * Throw the dice.
 *
 * From `idle` that is all six. From `pick` it commits the current selection
 * first — the two are one move on purpose, because "set aside and throw" is one
 * decision at the table and splitting it into two messages gives a flaky phone
 * somewhere to land between them.
 */
export function roll(g: GameState, playerId: string): void {
  const player = requireTurn(g, playerId);
  if (g.phase === 'farkle') throw new RuleError('the turn is over');

  // Throwing six is how you decline the leftovers. Clearing the offer here rather
  // than in `advance` is what makes it expire on the first throw either way, so
  // there is never a moment where the table shows dice that are no longer there.
  g.carry = null;

  if (g.phase === 'pick') {
    if (!canRollAgain(g)) throw new RuleError('set aside at least one scoring die first');
    commitHeld(g, player);
  }

  // Hot dice: every die is set aside, so all six come back. This is the run that
  // makes people lose 4,000 points, and it is the whole appeal of the game.
  if (g.dice.every((d) => d.state === 'set')) {
    for (const d of g.dice) d.state = 'live';
    g.hotStreak += 1;
    log(g, { kind: 'hot', playerId: player.id, text: `${player.name} is on hot dice` });
  }

  for (const d of g.dice) if (d.state === 'live') d.face = d6(g);
  g.throwId += 1;

  const thrown = liveFaces(g);
  if (isFarkle(thrown)) {
    g.phase = 'farkle';
    log(g, { kind: 'farkle', playerId: player.id, text: `${player.name} farkled`, points: g.turnPoints });
    return;
  }
  g.phase = 'pick';
  log(g, { kind: 'roll', playerId: player.id, text: `${player.name} threw ${thrown.length}` });
}

/**
 * Take the dice the last player left, and throw them.
 *
 * One move, not two, for the same reason `roll` commits the selection it throws:
 * "pick these up and throw them" is a single decision at the table, and there is
 * no legal state in between worth being able to sit in. It also means the risk
 * lands immediately — you cannot inherit 1,100 and then bank it without ever
 * having rolled, which would make the rule strictly free money.
 */
export function takeCarry(g: GameState, playerId: string): void {
  const player = requireTurn(g, playerId);
  if (!g.carry) throw new RuleError('there is nothing left on the table');
  if (!canPiggyback(g)) throw new RuleError('you have to be on the board to pick those up');
  const carry = g.carry;

  // Their set-aside dice become yours, showing the same faces. The rest of the
  // slots go back in the cup — six total, always, so the hot-dice check in `roll`
  // keeps working: clear what you inherited and the whole table comes back.
  g.dice = [
    ...carry.kept.map((face) => ({ face, state: 'set' as DieState })),
    ...Array.from({ length: carry.dice }, () => ({ face: 1, state: 'live' as DieState })),
  ];
  g.turnPoints = carry.points;
  log(g, {
    kind: 'carry',
    playerId: player.id,
    text: `${player.name} took ${carry.fromName}'s ${carry.dice} ${carry.dice === 1 ? 'die' : 'dice'} and ${carry.points.toLocaleString('en-US')}`,
    points: carry.points,
  });

  roll(g, playerId);
}

/** Move the held selection into the set-aside pile and score it. */
function commitHeld(g: GameState, player: Player) {
  const held = heldFaces(g);
  const score = scoreKeep(held);
  if (score === null || held.length === 0) throw new RuleError('that is not a scoring set');
  for (const d of g.dice) if (d.state === 'held') d.state = 'set';
  g.turnPoints += score;
  log(g, { kind: 'keep', playerId: player.id, text: `${player.name} set aside ${score}`, points: score });
}

/**
 * Add or remove one die from the selection.
 *
 * Deliberately permissive: any live die may be picked up, including one that
 * makes the set illegal. Refusing the tap is worse than showing why it does not
 * work — a player reaching for three 2s has to be allowed to place the first
 * two, and a rule that only lets you build legal sets one die at a time makes
 * three-of-a-kind unreachable.
 */
export function toggle(g: GameState, playerId: string, index: number): void {
  requireTurn(g, playerId);
  if (g.phase !== 'pick') throw new RuleError('there is nothing to pick');
  const die = g.dice[index];
  if (!die) throw new RuleError('no such die');
  if (die.state === 'set') throw new RuleError('that die is already set aside');
  die.state = die.state === 'held' ? 'live' : 'held';
}

/** Take the whole selection back. */
export function clearHeld(g: GameState, playerId: string): void {
  requireTurn(g, playerId);
  if (g.phase !== 'pick') throw new RuleError('there is nothing to pick');
  for (const d of g.dice) if (d.state === 'held') d.state = 'live';
}

/**
 * Bank the turn.
 *
 * Commits the selection, adds the total to the score, and hands over. This is
 * also where the game can end, which is why the threshold check lives in
 * `canBank` and is re-asserted here: the button being greyed out is a courtesy,
 * not the enforcement.
 */
export function bank(g: GameState, playerId: string): void {
  const player = requireTurn(g, playerId);
  if (g.phase !== 'pick') throw new RuleError('nothing to bank');
  if (!canBank(g)) {
    throw new RuleError(
      player.onBoard ? 'that is not a scoring set' : `you need ${g.rules.threshold} in one turn to get on the board`,
    );
  }
  commitHeld(g, player);

  const gained = g.turnPoints;
  player.score += gained;
  player.onBoard = true;
  player.turns.push(gained);
  log(g, { kind: 'bank', playerId: player.id, text: `${player.name} banked ${gained}`, points: gained });

  // What they leave behind for the next player, if the house plays that way.
  //
  // `points` is a copy, not a transfer: they have already been paid, and the
  // whole shape of the rule is that stopping early hands somebody else a running
  // start. `kept` is only the dice currently showing — after a hot streak the
  // earlier cycles are already back in the cup, so the drawn dice can be worth
  // less than the number beside them. That is what the felt actually looks like.
  const left = g.dice.filter((d) => d.state !== 'set').length;
  const carry: Carry | null =
    g.rules.piggyback && left > 0
      ? {
          kept: g.dice.filter((d) => d.state === 'set').map((d) => d.face),
          dice: left,
          points: gained,
          fromId: player.id,
          fromName: player.name,
        }
      : null;

  if (g.closerId === null && player.score >= g.rules.target) {
    if (!g.rules.finalRound) {
      finish(g);
      return;
    }
    g.closerId = player.id;
    log(g, {
      kind: 'win',
      playerId: player.id,
      text: `${player.name} reached ${g.rules.target.toLocaleString('en-US')} — last turns`,
    });
  }
  advance(g, carry);
}

/**
 * End a farkled turn.
 *
 * A separate move rather than something `roll` does inline, because a farkle
 * deserves a beat on screen. Whoever is driving decides how long that beat is —
 * the browser gives you a second and a bit, the server gives a dropped player
 * three seconds and then moves on so the table is not held hostage.
 */
export function endTurn(g: GameState, playerId: string): void {
  const player = requireTurn(g, playerId);
  if (g.phase !== 'farkle') throw new RuleError('the turn is not over');
  player.turns.push(0);
  advance(g);
}

/**
 * Hand the turn on, optionally leaving something on the felt for whoever is next.
 *
 * The carry is applied AFTER `resetTurn`, which clears it — a turn that ends in a
 * farkle or a forfeit passes nothing, and defaulting to null here is what makes
 * that true by construction rather than by every caller remembering.
 */
function advance(g: GameState, carry: Carry | null = null) {
  const next = (g.turn + 1) % g.players.length;
  // The lap is complete the moment play would return to whoever closed it out.
  if (g.closerId !== null && g.players[next]!.id === g.closerId) {
    finish(g);
    return;
  }
  g.turn = next;
  if (next === 0) g.round += 1;
  resetTurn(g);
  g.carry = carry;
}

function resetTurn(g: GameState) {
  g.dice = freshDice();
  g.turnPoints = 0;
  g.hotStreak = 0;
  g.phase = 'idle';
  g.carry = null;
}

function finish(g: GameState) {
  const top = Math.max(...g.players.map((p) => p.score));
  g.winnerIds = g.players.filter((p) => p.score === top).map((p) => p.id);
  g.phase = 'over';
  resetTurnForOver(g);
  const names = g.players.filter((p) => g.winnerIds.includes(p.id)).map((p) => p.name);
  log(g, {
    kind: 'win',
    playerId: g.winnerIds[0] ?? '',
    text: names.length > 1 ? `${names.join(' & ')} tie at ${top.toLocaleString('en-US')}` : `${names[0]} wins`,
  });
}

/** Over keeps the dice as they lie — the last throw is worth looking at. */
function resetTurnForOver(g: GameState) {
  g.turnPoints = 0;
  g.carry = null;
}

/**
 * Play out a turn for a seat nobody is driving.
 *
 * Used when a player drops mid-turn and the host hands their dice to the
 * computer, so the table is never stuck on someone whose phone died in a tunnel.
 */
export function forfeitTurn(g: GameState, playerId: string): void {
  const player = requireTurn(g, playerId);
  if (g.phase === 'over') return;
  player.turns.push(0);
  log(g, { kind: 'farkle', playerId: player.id, text: `${player.name} passed` });
  advance(g);
}

// ── views ───────────────────────────────────────────────────────────────────

/**
 * A client's view of the game. In most hidden-information games this function is
 * the security boundary; in Farkle every die is face-up on the table, so the
 * only thing it removes is the PRNG state — which is exactly the one field that
 * would let a client see the next throw before throwing it.
 */
export function viewFor(g: GameState): View {
  const { rng: _rng, ...visible } = g;
  return structuredCloneish(visible);
}

/**
 * A deep copy that does not need `structuredClone` — the engine's state is plain
 * JSON by construction, and this runs on Node 22 and in a browser identically.
 * The copy matters: one process serves every room, and an aliased `dice` array
 * is how one table's throw shows up at another.
 */
function structuredCloneish<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

/** Standings, best first. Ties keep table order, which is stable across pushes. */
export function ranked(g: Pick<GameState, 'players'>): Player[] {
  return [...g.players].sort((a, b) => b.score - a.score);
}
